import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { useConsentsStore } from "@/stores/consentsStore";
import { useToast } from "@/hooks/use-toast";
import { PatientAPCMConsentCreate } from "@/lib/types";
import { useOrganizationsStore } from "@/stores/organizationsStore";

interface PatientConsentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  onSuccess?: () => void;
}

const PatientConsentModal: React.FC<PatientConsentModalProps> = ({
  open,
  onOpenChange,
  patientId,
  onSuccess,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createConsent } = useConsentsStore();
  const { selectedOrganizationId } = useOrganizationsStore();
  const { toast } = useToast();

  const form = useForm<PatientAPCMConsentCreate>({
    defaultValues: {
      patient_id: patientId,
      organization_id: selectedOrganizationId || "",
      consent_status: "CONSENTED",
      method_of_consent: "Provider",
      document_url: "",
      call_id: "",
    },
  });

  const handleSubmit = async (data: PatientAPCMConsentCreate) => {
    if (!selectedOrganizationId) {
      toast({
        title: "Error",
        description: "No organization selected",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await createConsent({
        ...data,
        organization_id: selectedOrganizationId,
        call_id: data.call_id === '' ? null : data.call_id,
        document_url: data.document_url === '' ? null : data.document_url,
      });

      toast({
        title: "Success",
        description: "Patient consent created successfully",
      });

      form.reset();
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to create patient consent:", error);
      toast({
        title: "Error",
        description: "Failed to create patient consent",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Patient Consent</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="consent_status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Consent Status</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="CONSENTED">Consented</SelectItem>
                      <SelectItem value="DECLINED">Declined</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="method_of_consent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Method of Consent</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value || undefined}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Provider">Provider</SelectItem>
                      <SelectItem value="verbal">Verbal</SelectItem>
                      <SelectItem value="written">Written</SelectItem>
                      <SelectItem value="electronic">Electronic</SelectItem>
                      <SelectItem value="implied">Implied</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="document_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Document URL (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value || ""}
                      placeholder="https://example.com/consent-document.pdf"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create Consent"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PatientConsentModal;
